// Settings — two tabs:
//   General       → layout preferences (swap panel sides)
//   Taste profile → the formalized taste profile that steers analyze
//
// The profile is written by Claude at the start of every analyze run and is
// editable here: the next analyze refines from whatever is saved.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import { apiJson } from "../api/client";
import { loadLayout, type PanelSide, saveLayout } from "../api/layout";
import { YoutubeSettingsSection } from "../youtube/YoutubeSettingsSection"; // experimental, removable

import "./Settings.css";

type Tab = "general" | "taste";

export function Settings() {
  const [tab, setTab] = useState<Tab>("general");

  return (
    <div className="settings">
      <div className="settings__inner">
        <div className="settings__head">
          <h1 className="settings__title">settings</h1>
          <Link to="/" className="settings__back">
            ← back to navigator
          </Link>
        </div>

        <div className="settings__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "general"}
            className={"settings__tab" + (tab === "general" ? " settings__tab--active" : "")}
            onClick={() => setTab("general")}
          >
            general
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "taste"}
            className={"settings__tab" + (tab === "taste" ? " settings__tab--active" : "")}
            onClick={() => setTab("taste")}
          >
            taste profile
          </button>
        </div>

        {tab === "general" ? <GeneralTab /> : <TasteTab />}
      </div>
    </div>
  );
}

function GeneralTab() {
  const [layout, setLayout] = useState<PanelSide>(loadLayout);

  function toggle() {
    const next: PanelSide = layout === "swapped" ? "default" : "swapped";
    setLayout(next);
    saveLayout(next);
  }

  return (
    <>
    <section className="settings__section">
      <h2 className="settings__section-title">layout</h2>
      <div className="settings__row">
        <div className="settings__row-text">
          <div className="settings__row-label">swap panel sides</div>
          <div className="settings__row-desc">
            put the list explorer on the right and the details panel on the left. the top
            bar and each panel stay the same — only the columns switch sides. takes effect
            when you return to the navigator.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={layout === "swapped"}
          className={"switch" + (layout === "swapped" ? " switch--on" : "")}
          onClick={toggle}
          aria-label="swap panel sides"
        >
          <span className="switch__knob" />
        </button>
      </div>
    </section>
    <YoutubeSettingsSection />
    </>
  );
}

interface TasteProfile {
  profile: string | null;
  updated_at: string | null;
}

function TasteTab() {
  const { data, mutate } = useSWR<TasteProfile>("/api/taste", (url: string) =>
    apiJson<TasteProfile>(url),
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data.profile ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const dirty = data != null && draft !== null && draft !== (data.profile ?? "");

  async function save() {
    if (!draft?.trim() || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const updated = await apiJson<TasteProfile>("/api/taste", {
        method: "PUT",
        body: JSON.stringify({ profile: draft.trim() }),
      });
      await mutate(updated, { revalidate: false });
      setDraft(updated.profile ?? "");
      setNote("saved");
    } catch {
      setNote("saving failed — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings__section">
      <h2 className="settings__section-title">taste profile</h2>
      <p className="settings__blurb">
        written by claude at the start of every analyze run, from your ratings, and used to
        steer the taste-based suggestions. you can edit it — the next analyze refines from
        whatever stands here, so your edits shape future suggestions.
      </p>

      {data == null ? (
        <div className="settings__empty">loading…</div>
      ) : data.profile == null && !draft ? (
        <div className="settings__empty">
          no profile yet — it will be written the first time analyze runs. you can also seed
          one yourself below.
        </div>
      ) : null}

      <textarea
        className="settings__editor"
        value={draft ?? ""}
        spellCheck={false}
        placeholder={"- **Loves** — …\n- **Avoids** — …"}
        onChange={(e) => {
          setDraft(e.target.value);
          setNote(null);
        }}
      />

      <div className="settings__actions">
        <span className="settings__meta">
          {note ??
            (data?.updated_at
              ? `last updated ${new Date(data.updated_at).toLocaleString()}`
              : "")}
        </span>
        <button
          type="button"
          className="settings__save"
          disabled={busy || !dirty || !draft?.trim()}
          onClick={() => void save()}
        >
          save
        </button>
      </div>
    </section>
  );
}
